import { useState } from 'react';
import { BUTTON_SIZES, Button } from 'ui/button';
import { Tooltip } from 'ui/tooltip';
import { MODAL_SIZES, Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { ListContextProvider } from 'ui/listLegacy';
import { EMAIL_DOMAIN_FIELD } from '../../../constants';
import { useModalsContext } from '../../../contexts/modals';
import { FieldsList } from './FieldsList';
import { useExportToCSV, useTranslation } from '../../../hooks';
import { UserField, ListItem } from '../../../types';
import { useUserFieldsContext } from '../../../contexts/userFields';
import { useDefaultSelectedFields } from './hooks/useDefaultSelectedFields';

export const testIds = {
  modal: 'segmentation-export-to-csv',
};

export const ExportToCSVModal = () => {
  const { t } = useTranslation();
  const defaultSelectedFields = useDefaultSelectedFields();
  const [selectedFields, setSelectedFields] = useState<string[]>(defaultSelectedFields);
  const { hideModal: closeModal } = useModalsContext();

  // Put prioritized fields at the top of the list
  const { prioritizedUserFields: reorderedItems = [] } = useUserFieldsContext();

  const parsedItems = reorderedItems.reduce((parsedFields: ListItem[], userField: UserField) => {
    const { title, field } = userField;
    const shouldIncludeField = !title.includes('.') && field !== EMAIL_DOMAIN_FIELD;
    return shouldIncludeField ? [...parsedFields, { id: field, name: title }] : parsedFields;
  }, []);

  const fields = parsedItems.map((item) => ({
    title: item.name,
    field: item.id,
    checked: !!selectedFields.includes(item.id),
  }));

  const exportToCSV = useExportToCSV(fields);

  return (
    <Modal id={testIds.modal} onClose={closeModal} isModalOpen size={MODAL_SIZES.MEDIUM}>
      <ModalHeader>{t('modal.exportToCSV.header')}</ModalHeader>
      <ModalBody>
        <ListContextProvider items={parsedItems}>
          <FieldsList selectedFields={selectedFields} setSelectedFields={setSelectedFields} />
        </ListContextProvider>
      </ModalBody>
      <ModalFooter>
        <Tooltip
          isUsedOnButton
          isEnabled={selectedFields.length === 0}
          tooltipContent={t('modal.exportToCSV.tooltip')}
        >
          <Button
            onClick={exportToCSV}
            size={BUTTON_SIZES.LARGE}
            isDisabled={selectedFields.length === 0}
          >
            {t('modal.exportToCSV.button')}
          </Button>
        </Tooltip>
      </ModalFooter>
    </Modal>
  );
};
